import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaEdit } from 'react-icons/fa';
import Flag from 'react-world-flags';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';

const CountriesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [countries, setCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [countryModal, setCountryModal] = useState({ 
    open: false, 
    isEdit: false, 
    formData: { name: '', iso: '' },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, country: null });
  const [isLoading, setIsLoading] = useState(true);

  const fetchCountries = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getCountries();
      setCountries(data);
    } catch (err) {
      showAlert('Failed to load countries.', 'error', err);
      console.error('Error fetching countries:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleSaveCountry = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (countryModal.isEdit) {
        if (!countryModal.originalId) {
          showAlert('Invalid country data for update', 'error');
          return;
        }
        
        await organizationApi.updateCountry(countryModal.originalId, {
          name: countryModal.formData.name,
          iso: countryModal.formData.iso
        });
        showAlert('Country updated successfully', 'success');
      } else {
        await organizationApi.createCountry({
          name: countryModal.formData.name,
          iso: countryModal.formData.iso
        });
        showAlert('Country added successfully', 'success');
      }
      
      await fetchCountries();
      setCountryModal({ 
        open: false, 
        isEdit: false, 
        formData: { name: '', iso: '' },
        originalId: null
      });
    } catch (err) {
      const action = countryModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} country.`, 'error', err);
      console.error(`Error ${action}ing country:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCountry = (country) => {
    setDeleteModal({ open: true, country });
  };

  const confirmDelete = async () => {
    const countryToDelete = deleteModal.country;
    setDeleteModal({ open: false, country: null });
    
    if (!countryToDelete?.id) {
      showAlert('Invalid country data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteCountry(countryToDelete.id);
      await fetchCountries();
      showAlert('Country deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting country:', err);
      showAlert(err.message || 'Failed to delete country.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCountries = countries.filter(country => {
    if (!searchTerm) return true;
    return country.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
           country.iso.toLowerCase().includes((searchTerm || '').toLowerCase());
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Countries Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Country"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            <HeaderButton onClick={() => setCountryModal({
              open: true,
              isEdit: false,
              formData: { name: '', iso: '' },
              originalId: null
            })}>
              <FaPlus /> Add Country
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredCountries.map((country) => (
                  <Grid 
                    item
                    key={country.id}
                  >
                    <Card
                      sx={{
                        width: '100%',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        flexGrow: 1,
                        padding: '16px !important',
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', marginBottom: '8px' }}>
                          <Box sx={{
                            width: '48px',
                            height: '32px',
                            marginRight: '48px',
                            '& img': {
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '2px'
                            }
                          }}>
                            <Flag code={country.iso} />
                          </Box>

                          <Box sx={{
                            display: 'flex',
                            gap: '8px',
                          }}>
                            <Button
                              onClick={() => {
                                setCountryModal({
                                  open: true,
                                  isEdit: true,
                                  formData: { 
                                    name: country.name, 
                                    iso: country.iso 
                                  },
                                  originalId: country.id
                                });
                              }}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#0d6efd',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(13, 110, 253, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                  color: '#0d6efd'
                                }
                              }}
                            >
                              Edit
                            </Button>

                            <Button
                              onClick={() => handleDeleteCountry(country)}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#dc3545',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                  color: '#dc3545'
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>

                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            color: '#212529',
                          }}
                        >
                          {country.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      <Modal
        isOpen={countryModal.open}
        onClose={() => setCountryModal({
          open: false,
          isEdit: false,
          formData: { name: '', iso: '' },
          originalId: null
        })}
        onSubmit={handleSaveCountry}
        title={countryModal.isEdit ? "Edit Country" : "Add New Country"}
        submitText={countryModal.isEdit ? "Update Country" : "Add Country"}
      >
        <Box mb={2}>
          <TextField
            id="country-name"
            name="name"
            label="Country Name"
            required
            value={countryModal.formData.name}
            onChange={(e) => setCountryModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter country name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>

        <Box mb={2}>
          <TextField
            id="country-iso"
            name="iso"
            label="ISO Code"
            value={countryModal.formData.iso}
            onChange={(e) => setCountryModal(prev => ({
              ...prev,
              formData: { ...prev.formData, iso: e.target.value.toUpperCase() }
            }))}
            placeholder="e.g., US"
            autoComplete="off"
            variant="outlined"
          />
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, country: null })}
        onConfirm={confirmDelete}
        title="Delete Country"
        message={`Are you sure you want to delete ${deleteModal.country?.name}?`}
        actionButtonText="Delete"
      />
    </Container>
  );
};

export default CountriesConfiguration;